import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { CategoriaModal } from '../components/financeiro/CategoriaModal';
import type { CategoriaDto } from '@prometeus/contracts';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';

export const Route = createFileRoute('/w/$workspaceId/financeiro/configuracoes')({
  component: ConfiguracoesPage,
});

const listShell =
  'divide-y divide-black/[0.06] overflow-hidden rounded-[10px] bg-[#FFFFFF] shadow-[0_0_15px_rgba(0,0,0,0.05)] dark:divide-white/10 dark:bg-card dark:shadow-[0_0_15px_rgba(0,0,0,0.05)]';

const tipoLabel = (t: string) =>
  t === 'ENTRADA' ? 'Entrada' : t === 'SAIDA' ? 'Saída' : 'Ambos';

function ConfiguracoesPage() {
  const { workspaceId } = Route.useParams();
  const { hasPermission } = usePermissions();
  const canManageCategorias = hasPermission('financeiro.categorias.manage');
  const qc = useQueryClient();
  const { confirm } = useConfirmDialog();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CategoriaDto | null>(null);

  const { data: categorias = [] } = useQuery<CategoriaDto[]>({
    queryKey: ['financeiro', workspaceId, 'categorias'],
    queryFn: async () => {
      const res = await api.get('/financeiro/categorias');
      return res.data.data ?? res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/financeiro/categorias/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['financeiro', workspaceId, 'categorias'] }),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-5 rounded-2xl border-0 bg-[#FFFFFF] p-5 shadow-[0_0_15px_rgba(0,0,0,0.05)] outline-none ring-0 dark:bg-card dark:shadow-[0_0_15px_rgba(0,0,0,0.05)] sm:p-6 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-sans text-base font-normal tracking-normal text-[#06141B] dark:text-foreground">
            Configurações do financeiro
          </h2>
          <p className="mt-1 max-w-2xl font-sans text-xs font-normal text-[#9BA8AB] dark:text-muted-foreground">
            Gerencie categorias usadas nos lançamentos de entrada e saída.
          </p>
        </div>
        {canManageCategorias && (
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
            className="inline-flex shrink-0 items-center gap-2 rounded-[10px] bg-[#0a1118] px-4 py-2.5 text-[12px] font-medium text-white transition-opacity hover:opacity-90 dark:bg-foreground dark:text-background"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <span className="text-base leading-none" aria-hidden>
              +
            </span>
            Nova categoria
          </button>
        )}
      </div>

      <div className={listShell} style={{ fontFamily: 'Poppins, sans-serif' }}>
        {categorias.map((c) => (
          <div
            key={c.id}
            className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 transition-colors hover:bg-[#F8FAFC]/80 dark:hover:bg-muted/20"
          >
            <div className="flex min-w-0 items-center gap-2">
              {c.cor && (
                <span className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/20" style={{ backgroundColor: c.cor }} />
              )}
              <span className="text-[12px] font-medium text-[#06141B] dark:text-foreground">{c.nome}</span>
              <span className="text-[11px] text-[#9BA8AB] dark:text-muted-foreground">({tipoLabel(c.tipo)})</span>
            </div>
            {canManageCategorias && (
              <div className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(c);
                    setShowModal(true);
                  }}
                  className="text-[12px] font-medium text-[#06141B] underline decoration-[rgba(6,20,27,0.25)] underline-offset-2 hover:opacity-80 dark:text-foreground dark:decoration-white/30"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Excluir categoria?',
                      description: 'Esta ação removerá a categoria selecionada.',
                      confirmLabel: 'Excluir',
                      cancelLabel: 'Cancelar',
                      variant: 'danger',
                    });
                    if (ok) deleteMutation.mutate(c.id);
                  }}
                  className="text-[12px] font-medium text-red-600 underline decoration-red-600/30 underline-offset-2 hover:opacity-80 dark:text-red-400 dark:decoration-red-400/30"
                >
                  Excluir
                </button>
              </div>
            )}
          </div>
        ))}
        {categorias.length === 0 && (
          <p className="px-4 py-10 text-center text-[12px] text-muted-foreground">Nenhuma categoria cadastrada.</p>
        )}
      </div>

      <CategoriaModal
        open={showModal}
        onClose={() => setShowModal(false)}
        categoria={editing}
        workspaceId={workspaceId}
      />
    </div>
  );
}
